// src/components/Responsabile/ResponsabileCantieri.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

// Import componenti rapportini
import ResponsabileRapportiniManagement from './ResponsabileRapportiniManagement';
import ResponsabileGalleriaCantiere from './ResponsabileGalleriaCantiere';

// Import CSS unificato (stesso degli admin)
import '../Admin/Cantieri/CantieriManagement.css';

const ResponsabileCantieri = () => {
  const { currentUser } = useAuth();
  
  // Stati principali
  const [cantieri, setCantieri] = useState([]);
  const [filteredCantieri, setFilteredCantieri] = useState([]);
  const [committenti, setCommittenti] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCantiere, setSelectedCantiere] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // NUOVO: Stati per gestione rapportini
  const [currentView, setCurrentView] = useState('cantieri'); // 'cantieri', 'rapportini', 'galleria'

  // Filtri - AGGIORNATO: stato predefinito "Attivo"
  const [filters, setFilters] = useState({
    searchTerm: '',
    committenteId: '',
    stato: 'Attivo',
    impresa: ''
  });

  // Caricamento dati iniziale
  useEffect(() => {
    loadInitialData();
  }, [currentUser]);

  // Applicazione filtri
  useEffect(() => {
    applyFilters();
  }, [cantieri, filters]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      
      const [cantieriData, committentiData] = await Promise.all([
        getCantieri(),
        getCommittenti()
      ]);
      
      setCantieri(cantieriData);
      setCommittenti(committentiData);
      
      console.log(`Caricati: ${cantieriData.length} cantieri, ${committentiData.length} committenti`);
    } catch (error) {
      console.error('Errore nel caricamento dati:', error);
      showNotification('Errore nel caricamento dei dati', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getCantieri = async () => {
    try {
      const cantieriRef = collection(db, 'cantieri');
      const q = query(
        cantieriRef,
        where('responsabiliCantiere', 'array-contains', currentUser.uid),
        orderBy('nomeCommessa', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      const processDate = (dateField) => {
        if (!dateField) return null;
        
        // Se è un Timestamp di Firestore
        if (dateField.toDate && typeof dateField.toDate === 'function') {
          return dateField.toDate();
        }
        
        // Se è già una stringa o Date
        if (typeof dateField === 'string' || dateField instanceof Date) {
          return new Date(dateField);
        }
        
        // Se è un oggetto con seconds (formato Timestamp serializzato)
        if (dateField.seconds) {
          return new Date(dateField.seconds * 1000);
        }
        
        return null;
      };
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          dataInizioLavori: processDate(data.dataInizioLavori),
          dataFineLavori: processDate(data.dataFineLavori),
          createdAt: processDate(data.createdAt) || new Date(),
          updatedAt: processDate(data.updatedAt) || new Date()
        };
      });
    } catch (error) {
      console.error('Errore nel recupero cantieri:', error);
      return [];
    }
  };

  const getCommittenti = async () => {
    try {
      const committentiRef = collection(db, 'committenti');
      const q = query(committentiRef, orderBy('ragioneSociale', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        };
      });
    } catch (error) {
      console.error('Errore nel recupero committenti:', error);
      return [];
    }
  };

  const applyFilters = () => {
    let filtered = [...cantieri];

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(cantiere =>
        cantiere.nomeCommessa.toLowerCase().includes(searchLower) ||
        (cantiere.luogo && cantiere.luogo.toLowerCase().includes(searchLower)) ||
        (cantiere.contratto && cantiere.contratto.toLowerCase().includes(searchLower))
      );
    }

    if (filters.committenteId) {
      filtered = filtered.filter(cantiere => cantiere.committenteId === filters.committenteId);
    }

    if (filters.stato !== 'all') {
      filtered = filtered.filter(cantiere => cantiere.stato === filters.stato);
    }

    if (filters.impresa) {
      filtered = filtered.filter(cantiere => cantiere.impresa === filters.impresa);
    }

    setFilteredCantieri(filtered);
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // ========================================
  // NUOVE FUNZIONI PER GESTIRE RAPPORTINI
  // ========================================
  
  const handleGestisciRapportini = (cantiere) => {
    console.log('🔴 RAPPORTINI ACTION:', cantiere.nomeCommessa);
    setSelectedCantiere(cantiere);
    setCurrentView('rapportini');
  };

  const handleBackFromRapportini = () => {
    setCurrentView('cantieri');
    setSelectedCantiere(null);
  };

  const handleViewGalleria = (cantiere) => {
    console.log('🔴 GALLERIA ACTION:', cantiere.nomeCommessa);
    setSelectedCantiere(cantiere);
    setCurrentView('galleria');
  };

  const handleBackFromGalleria = () => {
    setCurrentView('cantieri');
    setSelectedCantiere(null);
  };

  const handleViewDetails = (cantiere) => {
    setSelectedCantiere(cantiere);
  };

  const formatDate = (date) => {
    if (!date) return 'Non specificata';
    return new Date(date).toLocaleDateString('it-IT');
  };

  const getCommittenteName = (committenteId) => {
    const committente = committenti.find(c => c.id === committenteId);
    return committente ? committente.ragioneSociale : 'Committente non trovato';
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Caricamento cantieri...</p>
      </div>
    );
  }

  // ========================================
  // RENDERING VISTE RAPPORTINI
  // ========================================
  
  // Vista gestione rapportini
  if (currentView === 'rapportini' && selectedCantiere) {
    return (
      <ResponsabileRapportiniManagement 
        cantiere={selectedCantiere}
        onBack={handleBackFromRapportini}
        onViewGalleria={() => setCurrentView('galleria')}
      />
    );
  }

  // Vista galleria
  if (currentView === 'galleria' && selectedCantiere) {
    return (
      <ResponsabileGalleriaCantiere 
        cantiere={selectedCantiere}
        onBack={handleBackFromGalleria}
      />
    );
  }

  return (
    <div className="cantieri-management">
      {/* Notifiche - SEMPRE visibili */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* VISTA DETTAGLIO CANTIERE STANDARD (da tabella cantieri) */}
      {selectedCantiere && (
        <CantiereDetail 
          cantiere={selectedCantiere}
          committenteName={getCommittenteName(selectedCantiere.committenteId)}
          onBack={() => setSelectedCantiere(null)}
          onGestisciRapportini={() => handleGestisciRapportini(selectedCantiere)}
          onViewGalleria={() => handleViewGalleria(selectedCantiere)}
          // 🔴 IMPORTANTE: Nascondi pulsante modifica per responsabili
          hideEditButton={true}
        />
      )}

      {/* VISTA PRINCIPALE - solo se non siamo in vista dettaglio */}
      {!selectedCantiere && (
        <>
          {/* Header */}
          <div className="section-header">
            <h2>🏗️ I Miei Cantieri</h2>
            <p>Gestisci i rapportini dei tuoi cantieri</p>
          </div>

          {/* Filtri */}
          <div className="filters-container">
            <div className="filters-grid">
              <div className="filter-group">
                <input
                  type="text"
                  placeholder="Cerca per nome cantiere, luogo o contratto..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                  className="filter-input"
                />
              </div>

              <div className="filter-group">
                <select
                  value={filters.committenteId}
                  onChange={(e) => setFilters({...filters, committenteId: e.target.value})}
                  className="filter-select"
                >
                  <option value="">Tutti i committenti</option>
                  {committenti.map(committente => (
                    <option key={committente.id} value={committente.id}>
                      {committente.ragioneSociale}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <select
                  value={filters.stato}
                  onChange={(e) => setFilters({...filters, stato: e.target.value})}
                  className="filter-select"
                >
                  <option value="all">Tutti gli stati</option>
                  <option value="Attivo">Attivi</option>
                  <option value="Archiviato">Archiviati</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tabella Cantieri */}
          {filteredCantieri.length > 0 ? (
            <>
              {/* TABELLA DESKTOP */}
              <div className="table-container hide-mobile">
                <table className="cantieri-table">
                  <thead>
                    <tr>
                      <th>Nome Cantiere</th>
                      <th>Luogo</th>
                      <th>Data Inizio</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCantieri.map(cantiere => (
                      <tr key={cantiere.id}>
                        <td>
                          <div className="cantiere-info">
                            <strong>{cantiere.nomeCommessa}</strong>
                            {cantiere.contratto && (
                              <small>Contratto: {cantiere.contratto}</small>
                            )}
                          </div>
                        </td>
                        <td>{cantiere.luogo || 'Non specificato'}</td>
                        <td>{formatDate(cantiere.dataInizioLavori)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn btn-info btn-sm"
                              onClick={() => handleViewDetails(cantiere)}
                              title="Visualizza dettagli"
                            >
                              👁️ Dettagli
                            </button>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleGestisciRapportini(cantiere)}
                              title="Gestisci rapportini"
                            >
                              📝 Rapportini
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* VISTA MOBILE */}
              <div className="cantieri-mobile-view show-mobile">
                {filteredCantieri.map(cantiere => (
                  <div key={cantiere.id} className="cantiere-mobile-card">
                    <div className="cantiere-mobile-card-header">
                      <h3 className="cantiere-mobile-card-title">
                        {cantiere.nomeCommessa}
                      </h3>
                    </div>
                    
                    <div className="cantiere-mobile-card-content">
                      <div className="cantiere-mobile-card-info">
                        <div className="cantiere-mobile-card-info-item">
                          <span className="cantiere-mobile-card-info-label">📍 Luogo:</span>
                          <span className="cantiere-mobile-card-info-value">
                            {cantiere.luogo || 'Non specificato'}
                          </span>
                        </div>
                        
                        <div className="cantiere-mobile-card-info-item">
                          <span className="cantiere-mobile-card-info-label">📅 Data Inizio:</span>
                          <span className="cantiere-mobile-card-info-value">
                            {formatDate(cantiere.dataInizioLavori)}
                          </span>
                        </div>
                        
                        {cantiere.contratto && (
                          <div className="cantiere-mobile-card-info-item">
                            <span className="cantiere-mobile-card-info-label">📄 Contratto:</span>
                            <span className="cantiere-mobile-card-info-value">
                              {cantiere.contratto}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="cantiere-mobile-card-actions">
                      <button 
                        className="btn btn-info"
                        onClick={() => handleViewDetails(cantiere)}
                        title="Visualizza dettagli"
                      >
                        👁️ Dettagli
                      </button>
                      
                      <button 
                        className="btn btn-primary"
                        onClick={() => handleGestisciRapportini(cantiere)}
                        title="Gestisci rapportini"
                      >
                        📝 Rapportini
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <h3>Nessun cantiere trovato</h3>
              <p>
                {cantieri.length === 0 ? 
                  'Non hai cantieri assegnati' : 
                  'Prova a modificare i filtri di ricerca'
                }
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// COMPONENTE DETTAGLIO CANTIERE PER RESPONSABILI (senza costi)
const CantiereDetail = ({ 
  cantiere, 
  committenteName, 
  onBack, 
  onGestisciRapportini,
  onViewGalleria,
  hideEditButton = false
}) => {
  const formatDate = (date) => {
    if (!date) return 'Non specificata';
    return new Date(date).toLocaleDateString('it-IT');
  };

  return (
    <div className="cantiere-detail">
      <div className="detail-header">
        <button className="btn-back" onClick={onBack}>
          ← Torna ai Cantieri
        </button>
        <h2>🏗️ {cantiere.nomeCommessa}</h2>
        <div className="header-actions">
          <button 
            className="btn btn-success"
            onClick={onViewGalleria}
          >
            🖼️ Galleria
          </button>
          <button 
            className="btn btn-primary"
            onClick={onGestisciRapportini}
          >
            📝 Gestisci Rapportini
          </button>
        </div>
      </div>

      <div className="cantiere-info">
        <div className="info-card">
          <h3>Informazioni Generali</h3>
          <div className="info-grid">
            <div className="info-item">
              <strong>Committente:</strong> {committenteName}
            </div>
            <div className="info-item">
              <strong>Stato:</strong> 
              <span className={`status-badge ${cantiere.stato.toLowerCase()}`}>
                {cantiere.stato}
              </span>
            </div>
            {cantiere.contratto && (
              <div className="info-item">
                <strong>Contratto:</strong> {cantiere.contratto}
              </div>
            )}
            {cantiere.luogo && (
              <div className="info-item">
                <strong>Luogo:</strong> {cantiere.luogo}
              </div>
            )}
            {cantiere.impresa && (
              <div className="info-item">
                <strong>Impresa:</strong> {cantiere.impresa}
              </div>
            )}
            {cantiere.direttoreLavori && (
              <div className="info-item">
                <strong>Direttore Lavori:</strong> {cantiere.direttoreLavori}
              </div>
            )}
            <div className="info-item">
              <strong>Data Inizio:</strong> {formatDate(cantiere.dataInizioLavori)}
            </div>
            {cantiere.dataFineLavori && (
              <div className="info-item">
                <strong>Data Fine:</strong> {formatDate(cantiere.dataFineLavori)}
              </div>
            )}
            {cantiere.note && (
              <div className="info-item full-width">
                <strong>Note:</strong> {cantiere.note}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rapportini-section">
        <div className="section-header">
          <h3>📝 Rapportini</h3>
          <div className="section-actions">
            <button 
              className="btn btn-secondary"
              onClick={onViewGalleria}
            >
              🖼️ Galleria
            </button>
            <button 
              className="btn btn-primary"
              onClick={onGestisciRapportini}
            >
              📝 Gestisci Rapportini
            </button>
          </div>
        </div>
        
        <div className="preview-content">
          <div className="preview-card">
            <h4>🎯 Sistema Rapportini</h4>
            <p>Clicca su "Gestisci Rapportini" per accedere alle funzionalità:</p>
            <ul>
              <li>Creazione e modifica rapportini giornalieri</li>
              <li>Gestione personale, mezzi e materiali</li>
              <li>Upload di foto e documenti</li>
              <li>Lavorazioni eseguite</li>
              <li>Galleria immagini del cantiere</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponsabileCantieri;